import type { HelpGuide } from '../types';

export const empbenGuide: HelpGuide = {
  key: 'empben',
  title: 'ผลประโยชน์พนักงาน (TAS 19)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ ผลประโยชน์พนักงาน TAS 19',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/empben/คู่มือการใช้งาน_ผลประโยชน์พนักงาน-TAS19_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ผลประโยชน์พนักงาน (TAS 19 Employee Benefits)” ใช้คุมภาระผูกพันผลประโยชน์พนักงานหลังออกจากงาน (โครงการผลประโยชน์ที่กำหนดไว้ — Defined Benefit Plan เช่น ค่าชดเชยเกษียณตามกฎหมายแรงงาน) ตามมาตรฐาน TAS 19 ซึ่งกำหนดให้กิจการรับรู้ “ภาระผูกพันตามโครงการ (DBO)” ไว้ในงบ แล้วทยอยรับรู้ ต้นทุนบริการปัจจุบัน (SC) + ดอกเบี้ย (IC) ทุกงวด โดยอ้างอิงรายงานของนักคณิตศาสตร์ประกันภัย (Actuary)',
        },
        {
          kind: 'callout',
          text: 'หลักการ TAS 19 (PAE) — Defined Benefit Plan: ① ต้นทุนบริการปัจจุบัน (Service Cost) — ภาระที่เพิ่มจากการทำงานของพนักงานในงวด ลง P&L · ② ดอกเบี้ยจากภาระผูกพัน (Interest Cost) — คิดดอกเบี้ยจาก DBO ต้นงวด ลง P&L · ③ กำไร/ขาดทุนจากการประมาณการตามหลักคณิตศาสตร์ (Actuarial Gain/Loss) — ลง OCI (ไม่ผ่าน P&L) · ④ ผลประโยชน์ที่จ่ายจริง (Benefit Paid) — ลดยอด DBO เมื่อจ่ายเงินให้พนักงานที่เกษียณ',
        },
        { kind: 'para', text: 'หน้าจอมี 5 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Dashboard — ',
              text: 'ตารางการเคลื่อนไหวของ DBO (YTD), สมมติฐาน Actuary และผลกระทบต่อ P&L / OCI / งบแสดงฐานะการเงิน (แท็บเริ่มต้น)',
            },
            {
              lead: 'รายงาน Actuary — ',
              text: 'ทะเบียนรายงานการประเมินของนักคณิตศาสตร์ประกันภัย (Actuarial Valuation)',
            },
            {
              lead: 'รับรู้รายงวด — ',
              text: 'ตารางรับรู้ SC + IC รายเดือน พร้อมขั้นตอนเสนอ-อนุมัติ (maker-checker)',
            },
            {
              lead: 'การเกษียณจริง / Settlement — ',
              text: 'ทะเบียนการจ่ายค่าชดเชยเมื่อพนักงานพ้นสภาพ พร้อมส่วนต่าง (Settlement Gain/Loss)',
            },
            {
              lead: 'หมายเหตุประกอบงบ — ',
              text: 'หมายเหตุตาม TAS 19 (กระทบยอด DBO, สมมติฐาน, ค่าใช้จ่าย P&L, Sensitivity, Maturity) + กระทบยอด GL + เครื่องมือ What-if',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/empben/01-overview-kpi.png',
          caption:
            'ภาพรวม — การ์ดสรุป 4 ใบ (DBO ปัจจุบัน / จำนวนพนักงาน / ค่าใช้จ่ายเดือน SC+IC / จ่ายผลประโยชน์ YTD) และบล็อกหลักการ TAS 19 (SC / IC / Actuarial G-L / Benefit Paid)',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ภาระผูกพัน DBO ปัจจุบัน (20170027) — ',
              text: 'ยอดภาระผูกพันตามโครงการ (บาท) ตามรายงาน Actuary ล่าสุด',
            },
            {
              lead: 'จำนวนพนักงานในแผน — ',
              text: 'จำนวนพนักงานที่อยู่ในโครงการ (คน)',
            },
            {
              lead: 'ค่าใช้จ่ายประจำเดือน (SC + IC) — ',
              text: 'ค่าใช้จ่ายที่รับรู้ต่อเดือน (ต้นทุนบริการ + ดอกเบี้ย)',
            },
            {
              lead: 'ค่าใช้จ่าย YTD (P&L) — ',
              text: 'ค่าใช้จ่ายสะสมที่รับรู้ในปีนี้ + ยอดผลประโยชน์ที่จ่ายจริง',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้ KPI มีแผง “หลักการ TAS 19 (PAE) — Defined Benefit Plan” สรุป ①SC ②IC ③Actuarial G/L ④Benefit Paid ปุ่มมุมขวาบน: เลือกปี · Upload Actuarial Report (อัปโหลดรายงาน Actuary) · Export Disclosure (ส่งออกหมายเหตุเป็น .xlsx) · รัน TAS 19 ประจำเดือน (ปุ่มสีแดง) สลับมุมมองด้วยแท็บด้านบนของตาราง',
        },
      ],
    },
    {
      id: 'dashboard',
      heading: '3. แท็บ Dashboard — การเคลื่อนไหว DBO และผลกระทบงบ',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Dashboard สรุปสิ่งที่โมดูลนี้ผลิตในที่เดียว: ตาราง “การเคลื่อนไหวของ DBO — YTD” (คอลัมน์ รายการ · บัญชี · จำนวน จบด้วยแถว ยอดคงเหลือ ณ ปัจจุบัน), ตาราง สมมติฐาน Actuary (วิธีการคำนวณ · อัตราคิดลด (Discount Rate) · อัตราเพิ่มเงินเดือน · อัตราการลาออก (Turnover) · ตารางอัตรามรณะ · อายุเกษียณ · เกณฑ์ค่าชดเชย)',
        },
        {
          kind: 'para',
          text: 'ด้านล่างมีตารางผลกระทบทางการเงิน 3 ใบ: งบกำไรขาดทุน (P&L), งบกำไรขาดทุนเบ็ดเสร็จอื่น (OCI) และงบแสดงฐานะการเงิน (SoFP) — ใช้ตรวจสอบยอดที่จะลงงบก่อนปิดบัญชี',
        },
        {
          kind: 'image',
          src: '/help/empben/02-dashboard-dbo-movement.png',
          caption:
            'Dashboard — ตารางการเคลื่อนไหว DBO (YTD), ตารางสมมติฐาน Actuary และตารางผลกระทบ P&L / OCI / งบแสดงฐานะการเงิน',
        },
      ],
    },
    {
      id: 'upload',
      heading: '4. การอัปโหลดรายงาน Actuarial ใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม Upload Actuarial Report (หรือ Upload รายงานใหม่ ในแท็บรายงาน Actuary) เพื่อเปิดโมดัล “Upload รายงาน Actuarial ใหม่” แล้วกรอก: Actuary/บริษัทผู้คำนวณ · ณ วันที่ประเมิน · เลขที่รายงาน (Report Ref) · สาขา (Branch) · หน่วยงาน (Cost Center)',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'สมมติฐานหลัก — ',
              text: 'Discount Rate · อัตราเพิ่มเงินเดือน · จำนวนพนักงานในแผน · DBO ที่คำนวณได้',
            },
            {
              lead: 'ตัวขับเคลื่อน IAS 19 — ',
              text: 'ต้นทุนบริการปัจจุบันรายปี (Projected SC) · DBO ต้นงวด (Opening DBO) · อัตราการลาออก · อายุเฉลี่ยถ่วงน้ำหนัก · ตารางมรณะ · อายุเกษียณ',
            },
            {
              lead: 'Sensitivity — ',
              text: 'ผลกระทบเมื่อปรับ อัตราคิดลด / อัตราเพิ่มเงินเดือน / อัตราการลาออก / ตารางมรณะ ทั้ง + และ −',
            },
            {
              lead: 'Maturity Profile — ',
              text: 'ประมาณการจ่ายผลประโยชน์ ภายใน 1 ปี / 1–2 ปี / 2–5 ปี / เกิน 5 ปี',
            },
            {
              lead: 'แนบรายงาน PDF — ',
              text: 'แนบไฟล์รายงานฉบับจริงจาก Actuary (ไม่บังคับ) เพื่อให้กด ดู PDF ได้ภายหลัง',
            },
          ],
        },
        {
          kind: 'para',
          text: 'กด บันทึก Valuation เพื่อบันทึก รายงานใหม่จะกลายเป็นฉบับ “ใช้งานปัจจุบัน” และไปอัปเดต DBO บน Dashboard/KPI ทันที',
        },
        {
          kind: 'image',
          src: '/help/empben/03-upload-valuation-modal.png',
          caption:
            'โมดัล Upload รายงาน Actuarial ใหม่ — สมมติฐานหลัก, ตัวขับเคลื่อน IAS 19, Sensitivity, Maturity Profile และการแนบ PDF',
        },
      ],
    },
    {
      id: 'actuary',
      heading: '5. แท็บรายงาน Actuary — ทะเบียนรายงาน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ รายงาน Actuary แสดงทะเบียนรายงานการประเมินทั้งหมด คอลัมน์: เลขที่รายงาน · ณ วันที่ · Actuary · วิธีการคำนวณ · Discount · Salary Inc · Turnover · DBO ที่คำนวณได้ · แผนที่ครอบคลุม · สถานะ · การกระทำ รายงานฉบับล่าสุดมีป้าย ใช้งานปัจจุบัน ส่วนฉบับเดิมเป็น เก็บประวัติ',
        },
        {
          kind: 'para',
          text: 'ปุ่มในแต่ละแถว: ดู PDF (เปิดไฟล์รายงานที่แนบไว้ในแท็บใหม่ — กดได้เมื่อมีไฟล์แนบ)',
        },
        {
          kind: 'image',
          src: '/help/empben/04-actuary-register.png',
          caption:
            'แท็บรายงาน Actuary — ทะเบียนรายงานพร้อมป้ายสถานะ ใช้งานปัจจุบัน / เก็บประวัติ และปุ่ม ดู PDF',
        },
      ],
    },
    {
      id: 'periodic',
      heading: '6. แท็บรับรู้รายงวด — รับรู้ SC/IC รายเดือน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ รับรู้รายงวด แสดงตารางการรับรู้ SC/IC รายเดือน คอลัมน์: งวด · วันที่ลงบัญชี · Service Cost (SC) · Interest Cost (IC) · รวมค่าใช้จ่าย · เลขที่ JV · ผู้รับรู้ · สถานะ · การกระทำ จบด้วยแถว รวม YTD แต่ละแถวแสดงสถานะ รับรู้แล้ว (พร้อมปุ่ม ดู JV) หรือ รอรับรู้ พร้อมปุ่มขั้นตอนอนุมัติ',
        },
        {
          kind: 'image',
          src: '/help/empben/05-periodic-accrual.png',
          caption:
            'แท็บรับรู้รายงวด — ตาราง SC/IC รายเดือน (รับรู้แล้ว + ดู JV / รอ พิจารณา) และแถวรวม YTD',
        },
      ],
    },
    {
      id: 'recognize',
      heading: '7. การรัน TAS 19 ประจำเดือน',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม รัน TAS 19 ประจำเดือน เพื่อเปิดโมดัลรับรู้ของงวด ระบบจะแสดงพรีวิวการแยกใบสำคัญ (JV split) ให้ตรวจก่อนเสนอ: เดบิต ต้นทุนบริการปัจจุบัน (SC), เดบิต ดอกเบี้ยจากภาระผูกพัน (IC) และเครดิต ภาระผูกพันผลประโยชน์พนักงาน',
        },
        {
          kind: 'para',
          text: 'กด เสนอรายการ (DRAFT) เพื่อสร้างรายการแบบร่าง รายการจะเข้าสู่ขั้นตอนเสนอ-อนุมัติ (maker-checker) — ยังไม่ลง JV จริงจนกว่าจะอนุมัติขั้นสุดท้าย',
        },
        {
          kind: 'image',
          src: '/help/empben/06-recognize-modal-jv.png',
          caption:
            'โมดัลรัน TAS 19 ประจำเดือน — ตาราง JV split (SC เดบิต / IC เดบิต / ภาระผูกพัน เครดิต)',
        },
      ],
    },
    {
      id: 'settlement',
      heading: '8. แท็บการเกษียณจริง / Settlement',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อพนักงานพ้นสภาพและต้องจ่ายค่าชดเชย ให้เปิดแท็บ การเกษียณจริง / Settlement ทะเบียนแสดงคอลัมน์: พนักงาน · แผนก/ตำแหน่ง · วันเริ่ม → วันเกษียณ · อายุงาน · เงินเดือนสุดท้าย · ค่าชดเชย (LPA) · Provision สะสม · ส่วนต่าง · สถานะ · การกระทำ แถวที่จ่ายแล้วเป็น จ่ายเงินแล้ว ส่วนแบบร่างเป็น DRAFT (รอจ่าย/ใกล้เกษียณ)',
        },
        {
          kind: 'para',
          text: 'กด บันทึก settlement เพื่อเปิดโมดัล กรอก รหัสพนักงาน · ชื่อ-นามสกุล · แผนก/ตำแหน่ง · สาเหตุการพ้นสภาพ (เกษียณอายุ / เลิกจ้าง (ม.ร.394) / ครบกำหนดสัญญา / ลาออก) · วันเริ่มงาน · วันพ้นสภาพ · เงินเดือนสุดท้าย · Provision สะสม',
        },
        {
          kind: 'image',
          src: '/help/empben/07-settlement-register.png',
          caption:
            'แท็บการเกษียณจริง / Settlement — ทะเบียน (จ่ายเงินแล้ว / DRAFT) พร้อม LPA / Provision / ส่วนต่าง',
        },
      ],
    },
    {
      id: 'settle-modal',
      heading: '9. โมดัลบันทึก Settlement และส่วนต่าง',
      blocks: [
        {
          kind: 'para',
          text: 'ในโมดัลบันทึก settlement เมื่อกรอกข้อมูลครบ ระบบจะคำนวณ ค่าชดเชยตามกฎหมาย (LPA) เทียบกับ Provision สะสม แล้วแสดง ส่วนต่าง (Settlement Gain/Loss) แบบเรียลไทม์ พร้อมพรีวิวตารางใบสำคัญ (JV) ที่จะลง',
        },
        {
          kind: 'callout',
          text: 'ถ้า LPA ที่ต้องจ่ายจริง มากกว่า Provision ที่ตั้งสะสมไว้ จะเกิด Settlement Loss (ขาดทุนจากการชำระ) ระบบลงรายการตามนโยบาย ALL_TO_PL (ผลต่างเข้า P&L ทั้งจำนวน) ตรวจส่วนต่างให้ตรงก่อนกด สร้างรายการ settlement (DRAFT)',
        },
        {
          kind: 'image',
          src: '/help/empben/08-settle-modal-preview.png',
          caption:
            'โมดัลบันทึก settlement — พรีวิว LPA vs Provision = ส่วนต่าง (Settlement Loss) และตาราง JV',
        },
      ],
    },
    {
      id: 'disclosure',
      heading: '10. แท็บหมายเหตุประกอบงบ (Disclosure)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ หมายเหตุประกอบงบ จัดทำหมายเหตุตาม TAS 19 ให้พร้อมปิดงบ 5 ส่วน: (1) การเปลี่ยนแปลงในมูลค่าปัจจุบันของภาระผูกพัน (กระทบยอด DBO ปีนี้/ปีก่อน) (2) สมมติฐานหลักในการคำนวณ (3) ค่าใช้จ่ายที่รับรู้ในกำไรหรือขาดทุน (4) การวิเคราะห์ความอ่อนไหว (Sensitivity) (5) ประมาณการจ่ายผลประโยชน์ในอนาคต (Maturity Profile) เลือกปีได้จากตัวเลือกปีมุมขวาบน และกด Export Disclosure เพื่อส่งออกเป็น .xlsx',
        },
        {
          kind: 'image',
          src: '/help/empben/09-disclosure.png',
          caption:
            'แท็บหมายเหตุประกอบงบ — กระทบยอด DBO, สมมติฐาน, ค่าใช้จ่าย P&L และการวิเคราะห์ความอ่อนไหว (Sensitivity)',
        },
      ],
    },
    {
      id: 'whatif',
      heading: '11. กระทบยอด GL และเครื่องมือ What-if',
      blocks: [
        {
          kind: 'para',
          text: 'ส่วนล่างของแท็บหมายเหตุประกอบงบแสดง Maturity Profile (ภายใน 1 ปี / 1–2 ปี / 2–5 ปี / เกิน 5 ปี / รวม / อายุเฉลี่ยถ่วงน้ำหนัก) และแบนเนอร์ กระทบยอด GL ที่จะเป็นสีเขียว ✓ เมื่อยอดภาระผูกพันใน GL ตรงกับยอดที่คำนวณได้ (ใช้ยืนยันว่าบัญชีกับหมายเหตุ tie-out ตรงกันก่อนปิดงบ)',
        },
        {
          kind: 'para',
          text: 'เครื่องมือ What-if ให้ทดลองปรับสมมติฐาน (เช่น อัตราคิดลด) แล้วกด ประเมิน DBO เพื่อดูว่า DBO ฐาน จะเปลี่ยนเป็น ประมาณการ เท่าไร โดยไม่กระทบข้อมูลจริง (ต้องมีรายงาน Actuary แล้วจึงจะใช้งานได้)',
        },
        {
          kind: 'image',
          src: '/help/empben/10-whatif-result.png',
          caption:
            'Disclosure ส่วนล่าง — Maturity Profile, แบนเนอร์กระทบยอด GL ตรงกัน ✓ และเครื่องมือ What-if (DBO ฐาน → ประมาณการ)',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '12. สรุปสถานะของรายการ',
      blocks: [
        {
          kind: 'para',
          text: 'รายงาน Actuary, การรับรู้รายงวด และ Settlement มีชุดสถานะของตัวเอง ดังนี้:',
        },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ใช้งานปัจจุบัน', 'รายงาน Actuary ฉบับล่าสุดที่ใช้อ้างอิงตอนนี้'],
            ['เก็บประวัติ', 'รายงาน Actuary ฉบับเดิมที่ถูกแทนที่แล้ว'],
            ['รอรับรู้', 'รายการรับรู้รายงวดที่ยังไม่ลง JV (รอเสนอ/อนุมัติ)'],
            ['รับรู้แล้ว', 'รับรู้รายงวดและลง JV เรียบร้อย (กด ดู JV ได้)'],
            ['กลับรายการแล้ว', 'รายการที่ถูกกลับรายการ (Reverse) ในงวดที่เลือก'],
            ['รอจ่าย', 'Settlement แบบร่าง (DRAFT) ที่ถึงกำหนดจ่ายแล้ว'],
            ['ใกล้เกษียณ', 'Settlement แบบร่างที่วันพ้นสภาพยังอยู่ในอนาคต'],
            ['จ่ายเงินแล้ว', 'Settlement ที่อนุมัติและลง JV จ่ายค่าชดเชยแล้ว'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '13. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไม Dashboard ถึงยังไม่มี DBO?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ต้อง Upload Actuarial Report อย่างน้อย 1 ฉบับก่อน ระบบจึงจะมียอด DBO และคำนวณ SC/IC ได้',
            },
          ],
        },
        { kind: 'para', text: 'กด รัน TAS 19 ประจำเดือน แล้ว ทำไม JV ยังไม่ขึ้น?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'การเสนอรายการสร้างเป็น DRAFT ก่อน ต้องผ่านขั้นตอน เสนอ → พิจารณา (อนุมัติ) จนครบ ระบบจึงลง JV จริงในขั้นอนุมัติสุดท้าย',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม ดู PDF / ประเมิน DBO กดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ดู PDF ใช้ได้เฉพาะรายงานที่แนบไฟล์ PDF ไว้ ส่วน ประเมิน DBO (What-if) ต้องมีรายงาน Actuary แล้วเท่านั้น',
            },
          ],
        },
        { kind: 'para', text: 'รับรู้ผิดงวดต้องทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ที่รายการสถานะ รับรู้แล้ว ใช้ปุ่ม กลับรายการ ระบุงวดที่จะลงรายการกลับ ระบบจะลง JV กลับด้านเข้างวดที่เปิดอยู่',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '14. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'TAS 19 — ',
              text: 'มาตรฐานการบัญชีเรื่องผลประโยชน์พนักงาน (รับรู้ภาระผูกพันโครงการผลประโยชน์ที่กำหนดไว้)',
            },
            {
              lead: 'DBO — ',
              text: 'ภาระผูกพันตามโครงการผลประโยชน์ (Defined Benefit Obligation) มูลค่าปัจจุบันของผลประโยชน์ที่ต้องจ่ายในอนาคต',
            },
            {
              lead: 'Service Cost (SC) — ',
              text: 'ต้นทุนบริการปัจจุบัน — ภาระที่เพิ่มจากการทำงานของพนักงานในงวด (ลง P&L)',
            },
            {
              lead: 'Interest Cost (IC) — ',
              text: 'ดอกเบี้ยจากภาระผูกพัน — คิดดอกเบี้ยจาก DBO ต้นงวดด้วยอัตราคิดลด (ลง P&L)',
            },
            {
              lead: 'Actuarial Gain/Loss — ',
              text: 'กำไร/ขาดทุนจากการประมาณการตามหลักคณิตศาสตร์ — รับรู้ใน OCI ไม่ผ่าน P&L',
            },
            {
              lead: 'Actuary — ',
              text: 'นักคณิตศาสตร์ประกันภัย ผู้จัดทำรายงานประเมิน DBO และสมมติฐาน',
            },
            {
              lead: 'Discount Rate — ',
              text: 'อัตราคิดลด ใช้คิดลดผลประโยชน์ในอนาคตเป็นมูลค่าปัจจุบัน และใช้คำนวณดอกเบี้ย (IC)',
            },
            {
              lead: 'LPA — ',
              text: 'ค่าชดเชยตามกฎหมายแรงงาน (Legal Provident/Severance) ที่ต้องจ่ายเมื่อพนักงานพ้นสภาพ',
            },
            {
              lead: 'Settlement Gain/Loss — ',
              text: 'ส่วนต่างระหว่างค่าชดเชยที่จ่ายจริง (LPA) กับ Provision สะสม รับรู้เข้า P&L (ALL_TO_PL)',
            },
            {
              lead: 'Maturity Profile — ',
              text: 'ประมาณการกระแสจ่ายผลประโยชน์ในอนาคตแยกตามช่วงเวลา (TAS 19.147(c))',
            },
            {
              lead: 'Sensitivity — ',
              text: 'การวิเคราะห์ความอ่อนไหว — ผลกระทบต่อ DBO เมื่อปรับสมมติฐาน ±',
            },
          ],
        },
      ],
    },
  ],
};
